/**
 * JWT utilities using jose library
 * Provides robust JWT decoding with proper error handling
 */
import { type JWTPayload, decodeJwt as joseDecodeJwt } from "jose";

/**
 * Extended JWT payload with common claims
 */
export interface JwtPayload extends JWTPayload {
  exp?: number;
  iat?: number;
  sub?: string;
  [key: string]: unknown;
}

/**
 * Decode a JWT token and extract the payload
 * Uses the jose library for robust, standards-compliant decoding
 *
 * @param token - The JWT token string
 * @returns The decoded payload or null if decoding fails
 */
export const decodeJwt = (token: string): JwtPayload | null => {
  try {
    if (!token || typeof token !== "string") {
      return null;
    }

    // jose's decodeJwt handles all the complexity of JWT parsing
    const payload = joseDecodeJwt(token);
    return payload as JwtPayload;
  } catch (error) {
    // jose throws JWTInvalid for malformed tokens
    console.error("Failed to decode JWT:", error instanceof Error ? error.message : error);
    return null;
  }
};

/**
 * Extract expiration timestamp from a JWT token
 *
 * @param token - The JWT token string
 * @returns Expiration timestamp in milliseconds, or null if not found
 */
export const getJwtExpiration = (token: string): number | null => {
  const decoded = decodeJwt(token);

  if (!decoded || typeof decoded.exp !== "number") {
    return null;
  }

  // JWT exp is in seconds, convert to milliseconds
  return decoded.exp * 1000;
};

/**
 * Check if a JWT token is expired
 *
 * @param token - The JWT token string
 * @returns true if expired or invalid, false if still valid
 */
export const isJwtExpired = (token: string): boolean => {
  const expiration = getJwtExpiration(token);
  if (expiration === null) {
    return true; // Treat tokens without expiration as expired
  }
  return Date.now() >= expiration;
};
